import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { IconButtonComponent, TextareaComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-input',
  standalone: true,
  imports: [ReactiveFormsModule, TextareaComponent, IconButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <form (submit)="$event.preventDefault(); submit()">
      <rt-textarea
        class="input"
        [formControl]="control"
        [placeholder]="placeholder"
        [autoHeight]="true"
        [maxHeight]="96"
        (keydown.enter)="onEnter($event)"
      />

      <div class="actions">
        @if (busy()) {
          <rt-icon-button iconName="stop" size="small" variant="primary" (click)="onStop()" />
        } @else {
          <rt-icon-button
            iconName="arrow-upward"
            size="small"
            variant="primary"
            [disabled]="!canSend()"
            (click)="submit()"
          />
        }
      </div>
    </form>
  `,
  styleUrl: './tellent-assistant-input.component.less',
})
export class TellentAssistantInputComponent {
  private readonly i18n = inject(I18nService);
  private readonly lastSent = signal<string>('');

  protected readonly control = new FormControl<string>('', { nonNullable: true });
  protected readonly draft = signal<string>('');
  protected readonly placeholder = this.i18n.translate('tlc.tellent_assistant.input.placeholder');

  public readonly busy = input<boolean>(false);
  public readonly send = output<string>();
  public readonly stop = output<void>();

  constructor() {
    this.control.valueChanges.subscribe(v => this.draft.set(v ?? ''));
  }

  public clear(): void {
    this.control.reset('');
    this.lastSent.set('');
  }

  protected canSend(): boolean {
    return !this.busy() && this.draft().trim().length > 0;
  }

  protected submit(): void {
    if (!this.canSend()) return;
    const text = this.draft().trim();
    this.lastSent.set(text);
    this.send.emit(text);
    this.control.reset('');
  }

  protected onEnter(event: Event): void {
    const ev = <KeyboardEvent>event;
    if (ev.shiftKey) return;
    ev.preventDefault();
    if (this.busy()) return;
    this.submit();
  }

  protected onStop(): void {
    this.stop.emit();
    const restored = this.lastSent();
    if (restored && this.draft().length === 0) {
      this.control.setValue(restored);
    }
    this.lastSent.set('');
  }
}
